import {
  baseCdcEventDataSchema,
  cdcAuthContextSchema,
  cdcEventModule,
  cdcEventType,
  cdcFullUpdateFields,
} from '@tempo-io/common-dto';
import { discriminatedUnion, z } from 'zod';

export const reportingStoreBaseCdcEventSchema = baseCdcEventDataSchema.extend({
  module: z.literal(cdcEventModule.enum['reporting-store']),
});

const itemHealthEntitySchema = z
  .object({
    itemId: z.number().int(),
  })
  .loose();

export const itemHealthDataChangeDTOSchema = discriminatedUnion('eventType', [
  reportingStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item-health'),
    ...cdcFullUpdateFields(itemHealthEntitySchema),
  }),
]);

const itemAggregationEntitySchema = z
  .object({
    itemId: z.number().int(),
  })
  .loose();

export const itemAggregationDataChangeDTOSchema = discriminatedUnion('eventType', [
  reportingStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item-aggregation'),
    ...cdcFullUpdateFields(itemAggregationEntitySchema),
  }),
]);

const objectiveHealthEntitySchema = z
  .object({
    objectiveId: z.number().int(),
  })
  .loose();

export const objectiveHealthDataChangeDTOSchema = discriminatedUnion('eventType', [
  reportingStoreBaseCdcEventSchema.extend({
    entityType: z.literal('objective-health'),
    ...cdcFullUpdateFields(objectiveHealthEntitySchema),
  }),
]);

const keyResultHealthEntitySchema = z
  .object({
    keyResultId: z.number().int(),
  })
  .loose();

export const keyResultHealthDataChangeDTOSchema = discriminatedUnion('eventType', [
  reportingStoreBaseCdcEventSchema.extend({
    entityType: z.literal('key-result-health'),
    ...cdcFullUpdateFields(keyResultHealthEntitySchema),
  }),
]);

const singleIdMirrorEntitySchema = z
  .object({
    id: z.number().int(),
  })
  .loose();

const scenarioItemMirrorEntitySchema = z
  .object({
    scenarioId: z.number().int(),
    itemId: z.number().int(),
  })
  .loose();

export const itemMirrorDataChangeDTOSchema = discriminatedUnion('eventType', [
  reportingStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item-mirror'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: singleIdMirrorEntitySchema,
  }),
  reportingStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item-mirror'),
    ...cdcFullUpdateFields(singleIdMirrorEntitySchema),
  }),
  reportingStoreBaseCdcEventSchema.extend({
    entityType: z.literal('item-mirror'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: singleIdMirrorEntitySchema,
  }),
]);

export const scenarioMirrorDataChangeDTOSchema = discriminatedUnion('eventType', [
  reportingStoreBaseCdcEventSchema.extend({
    entityType: z.literal('scenario-mirror'),
    ...cdcFullUpdateFields(singleIdMirrorEntitySchema),
  }),
]);

export const scenarioItemMirrorDataChangeDTOSchema = discriminatedUnion('eventType', [
  reportingStoreBaseCdcEventSchema.extend({
    entityType: z.literal('scenario-item-mirror'),
    ...cdcFullUpdateFields(scenarioItemMirrorEntitySchema),
  }),
  reportingStoreBaseCdcEventSchema.extend({
    entityType: z.literal('scenario-item-mirror'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: scenarioItemMirrorEntitySchema,
  }),
]);

export const scenarioItemScheduleMirrorDataChangeDTOSchema = discriminatedUnion('eventType', [
  reportingStoreBaseCdcEventSchema.extend({
    entityType: z.literal('scenario-item-schedule-mirror'),
    ...cdcFullUpdateFields(scenarioItemMirrorEntitySchema),
  }),
]);

const financeRecomputeEntitySchema = z
  .object({
    organizationId: z.string(),
  })
  .loose();

export const financeRecomputeDataChangeDTOSchema = discriminatedUnion('eventType', [
  reportingStoreBaseCdcEventSchema.extend({
    entityType: z.literal('finance-recompute'),
    ...cdcFullUpdateFields(financeRecomputeEntitySchema),
  }),
]);

export const reportingStoreEntityDataChangeDTOSchema = discriminatedUnion('entityType', [
  itemHealthDataChangeDTOSchema,
  itemAggregationDataChangeDTOSchema,
  financeRecomputeDataChangeDTOSchema,
  objectiveHealthDataChangeDTOSchema,
  keyResultHealthDataChangeDTOSchema,
  itemMirrorDataChangeDTOSchema,
  scenarioMirrorDataChangeDTOSchema,
  scenarioItemMirrorDataChangeDTOSchema,
  scenarioItemScheduleMirrorDataChangeDTOSchema,
]);

export const reportingStoreDataChangeDTOSchema = z.object({
  data: baseCdcEventDataSchema.and(reportingStoreEntityDataChangeDTOSchema),
  authContext: cdcAuthContextSchema,
  metadata: z.record(z.string(), z.string()).optional(),
});

export type ReportingStoreDataChangeDTO = z.output<typeof reportingStoreDataChangeDTOSchema>;

export type ItemHealthDataChangeDTO = Extract<
  ReportingStoreDataChangeDTO['data'],
  { entityType: 'item-health' }
>;

export type ItemAggregationDataChangeDTO = Extract<
  ReportingStoreDataChangeDTO['data'],
  { entityType: 'item-aggregation' }
>;

export type ObjectiveHealthDataChangeDTO = Extract<
  ReportingStoreDataChangeDTO['data'],
  { entityType: 'objective-health' }
>;

export type KeyResultHealthDataChangeDTO = Extract<
  ReportingStoreDataChangeDTO['data'],
  { entityType: 'key-result-health' }
>;
